<script setup lang="ts">
import type Guid from "@/app/types/common/guid";
import type { DialogEvents } from "@/components/mixins/dialogs";
import { useReactiveDialogScriptSetup } from "@/components/mixins/dialogs";
import { onBeforeMount } from "vue";
import messages, { keys } from "@/plugins/i18n";
import { makeRef, withLoading } from "@/components/composables/componentTools";
import {
  PlannedSalesVisit,
  salesVisitService,
} from "@/app/services/generated/salesvisitService";
import BmDialog from "@/components/dialogs/BmDialog.vue";
import { tenantService } from "@/app/services/extensions/tenantServiceExtended";

export interface CompleteVisitDialogProps {
  plannedSalesVisitId: Guid;
}

export interface CompleteVisitDialogEvents extends DialogEvents {}

const props = defineProps<CompleteVisitDialogProps>();
const emit = defineEmits<CompleteVisitDialogEvents>();

const { accept, cancel } = useReactiveDialogScriptSetup(emit);

const plannedSalesVisit = makeRef(new PlannedSalesVisit());

onBeforeMount(
  withLoading(async () => {
    if (props.plannedSalesVisitId !== null) {
      const plannedVisit = await salesVisitService.getPlannedSalesVisit(
        props.plannedSalesVisitId
      );
      const visitNextRepeatDate =
        await salesVisitService.getNextRepeatPlannedDate(
          props.plannedSalesVisitId
        );

      plannedSalesVisit.value = plannedVisit;
      plannedSalesVisit.value.date = visitNextRepeatDate ?? tenantService.today;
    }
  })
);

const completePlannedVisit = withLoading(async () => {
  await salesVisitService.savePlannedSalesVisit(plannedSalesVisit.value);
  accept();
});
</script>

<template>
  <bm-dialog
    :label="messages.sales_visit_update_plan"
    @accept="completePlannedVisit"
    @cancel="cancel"
  >
    <v-container>
      <v-row>
        <v-col>
          <bm-date-q
            :value.sync="plannedSalesVisit.date"
            :t="keys.sales_visit_next_visit"
            :rules="[$ruleSet.required()]"
          />
          <bm-time
            :value.sync="plannedSalesVisit.plannedTime"
            :t="keys.sales_visit_planned_time"
          />
          <bm-switch
            :value.sync="plannedSalesVisit.isPlannedTimeFlexible"
            :t="keys.sales_visit_is_planned_start_time_flexible"
          />
          <bm-number-q
            :value.sync="plannedSalesVisit.durationMinutes"
            :t="keys.sales_visit_duration"
            :min="0"
            :rules="[$ruleSet.notNegativeNumber()]"
            :suffix="$m.units_mins_suffix"
          />
        </v-col>
      </v-row>
    </v-container>
    <template #controls-confirm-text>
      {{ messages.update }}
    </template>
  </bm-dialog>
</template>
